import { Fragment } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  openNewProject,
  openColumnSettings,
  setProjectState,
  DashboardState,
} from 'app/slices/dashboardSlice';

/**
 * Container header (§3.D): "Dashboard" h3 + decorative cog, Add Project button,
 * the Manage Columns gear, and the Active/Archived/All state-toggle group
 * (dev delta 2026-06, container.tmpl). The modals themselves are built in a
 * later sub-phase — here the buttons just flip the dashboardSlice modal flags.
 *
 * `addDisabled` reflects permission gating (view/no_access ⇒ #addProject gets
 * .non-clickable). Permission wiring is finalized in a later sub-phase; the prop
 * exists so the shell can pass it through.
 */
interface ContainerHeaderProps {
  addDisabled?: boolean;
}

const STATES: DashboardState['projectState'][] = ['active', 'archived', 'all'];
const STATE_LABELS: Record<DashboardState['projectState'], string> = {
  active: 'Active',
  archived: 'Archived',
  all: 'All',
};

const ContainerHeader = ({ addDisabled = false }: ContainerHeaderProps) => {
  const dispatch = useAppDispatch();
  const projectState = useAppSelector((s) => s.dashboard.projectState);

  return (
    <div className="container-header">
      {/* container.tmpl:2 — the cog abuts "Dashboard" (no whitespace). */}
      <h3>
        Dashboard<i className="icon-cog" />
      </h3>
      <button
        id="addProject"
        className={`whiteboard-add-btn btn btn-add right${addDisabled ? ' non-clickable' : ''}`}
        type="button"
        onClick={() => dispatch(openNewProject())}
      >
        <span className="plus">Project</span>
      </button>
      <button
        className="setting-btn fa-cog icon-settings-1 right ka-has-tooltip"
        type="button"
        tooltip="Manage Columns"
        onClick={() => dispatch(openColumnSettings())}
      />
      <div className="state-toggle-group">
        {/* Whitespace parity (QA #2): container.tmpl:6-8 puts each span on its
            own line — the newline collapses to one space between the inline
            buttons. JSX strips it from mapped output, so restore it. */}
        {STATES.map((s, i) => (
          <Fragment key={s}>
            {i > 0 && ' '}
            <span
              className={`state-toggle-btn${projectState === s ? ' active' : ''}`}
              data-state={s}
              onClick={() => dispatch(setProjectState(s))}
            >
              {STATE_LABELS[s]}
            </span>
          </Fragment>
        ))}
      </div>
    </div>
  );
};

export default ContainerHeader;
